import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserId } from '~/components/Auth/useLoggedInUserId';
import { useLoggedInUserRoles } from '~/components/Auth/useLoggedInUserRoles';
import { useApplicationConfig } from '~/data/applicationConfig';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { Roles } from '~/data/user';
import {
  NotificationOptOutResponse,
  putNotificationOptOuts,
  useGetNotificationsOptOutSettings,
} from '~/data/userSettings';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useVisible } from '~/hooks/useVisible';
import { hasOneOfRoles } from '~/services/Permissions/roles';
import { getEnumName } from '~/utils/helpers/enum';

const NOTIFICATION_PREFERENCES_ROLES: Roles[] = [
  Roles.HR_ADMIN,
  Roles.HR_ADMIN_LIMITED,
  Roles.HR_STAFF,
  Roles.HR_DEV,
  Roles.HR_PEOP,
  Roles.HR_PEOP_DEV,
  Roles.HR_SALARY,
  Roles.HR_AUDITOR,
];

export const useNotificationsPreferencesCard = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const loggedInUserId = useLoggedInUserId();
  const userRoles = useLoggedInUserRoles();

  const { applicationConfig } = useApplicationConfig();
  const isEnabled = useMemo(() => {
    const featureEnabled =
      applicationConfig?.fComConfig?.miscellaneousConfig
        ?.fComEnableNotificationPreferences ?? false;

    return (
      featureEnabled && hasOneOfRoles(userRoles, NOTIFICATION_PREFERENCES_ROLES)
    );
  }, [applicationConfig, userRoles]);

  const { getEnums } = useAllEnums();
  const notificationAreaEnums = getEnums(EnumListKey.NOTIFICATION_AREA);

  const getAreaName = useCallback(
    (areaCode: string): string => {
      return getEnumName(notificationAreaEnums, areaCode) || areaCode;
    },
    [notificationAreaEnums],
  );

  const { isVisible, show, hide } = useVisible();

  const { data, isLoading, failedToLoad, mutate } =
    useGetNotificationsOptOutSettings(loggedInUserId, undefined, !isEnabled);

  const { areas } = useMemo(() => {
    const areas = !!data ? Object.keys(data) : [];

    return {
      areas,
    };
  }, [data]);

  const form = useForm({
    defaultValues: data,
  });
  const { reset, handleSubmit } = form;

  const hasDataChanged = useHasChanged(JSON.stringify(data));
  useEffect(() => {
    if (hasDataChanged) {
      reset(data);
    }
  }, [reset, hasDataChanged, data]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      return putNotificationOptOuts(data)
        .then((_response: NotificationOptOutResponse) => {
          mutate();
          hide();
          enqueueSnackbar(t('message_success_approval'), {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar(t('notificationPreferencesError'), {
            variant: 'error',
          });
        });
    });
  }, [t, enqueueSnackbar, handleSubmit, mutate, hide]);

  const onCancelEdit = useCallback(() => {
    reset(data);
    hide();
  }, [reset, data, hide]);

  return {
    form,
    onSubmit,
    editMode: isVisible,
    onEdit: show,
    onCancelEdit,
    areas,
    isEnabled,
    isLoading,
    getAreaName,
    failedToLoad,
  };
};
